/**
 * MfaVerify — segunda etapa do login para usuários com MFA ativo.
 *
 * Sprint 10 — corrige bloqueio total que acontecia após enrollment de MFA:
 * o backend retorna `mfa_required: true` + `mfa_token` (sem access_token).
 *
 * Esta tela (container; o visual é entrada/PainelMfa, design out/2026):
 *  1. Recebe `mfaToken` via `location.state` (NUNCA via localStorage — token
 *     é curto, 5 min, scope='mfa', e não serve como access_token). O e-mail
 *     digitado no login vem junto, só para ser mostrado mascarado.
 *  2. Coleta código TOTP (6 dígitos) ou de recuperação (XXXX-YYYY) — o mesmo
 *     campo `codigo` no backend, que tenta um e depois o outro.
 *  3. POST `/api/auth/mfa/verify` -> recebe access+refresh reais.
 *  4. Persiste sessão e redireciona para `location.state.from` ou o destino
 *     padrão (ou /primeiro-acesso).
 *
 * Sem `mfaToken`, volta para /login (acesso direto ou recarga da página).
 * Token expirado (5 min): trava o envio, avisa e volta ao /login sozinho.
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { DEFAULT_PATH } from "../../utils/rotas";
import PainelMfa from "./entrada/PainelMfa";
import { marcaDaEntrada } from "./entrada/marca";
import { mascararEmail } from "./entrada/logica";

// Bate com `MFA_TOKEN_LIFETIME_SECONDS` em autenticacao/views_mfa.py. Se o
// backend mudar, atualizar aqui — UX-only, o backend ainda valida o exp do JWT.
export const TEMPO_TOKEN_SEGUNDOS = 300;

// Bugfix 2026-05-28 (#8): ao zerar, volta sozinho ao /login depois de 2 s
// (tempo de ler o aviso de "tempo esgotado").
const ESPERA_APOS_EXPIRAR_MS = 2000;

// Tempo para o "Acesso liberado" (verde) aparecer antes de sair da tela.
const ESPERA_APOS_LIBERAR_MS = 600;

function useSegundosRestantes(ativo) {
  const [restante, setRestante] = useState(TEMPO_TOKEN_SEGUNDOS);
  useEffect(() => {
    if (!ativo) return undefined;
    const tick = setInterval(() => setRestante((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(tick);
  }, [ativo]);
  return restante;
}

export default function MfaVerify() {
  const { verifyMfaLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const mfaToken = location.state?.mfaToken || "";
  const destinoFinal = location.state?.from || DEFAULT_PATH;
  const primeiroAcesso = Boolean(location.state?.primeiroAcesso);
  const email = location.state?.email || "";
  const from = location.state?.from;

  const restante = useSegundosRestantes(Boolean(mfaToken));
  const expirado = restante <= 0;
  const timerSaida = useRef(0);

  useEffect(() => {
    if (!mfaToken) navigate("/login", { replace: true });
  }, [mfaToken, navigate]);

  useEffect(() => {
    if (!expirado) return undefined;
    const id = setTimeout(() => navigate("/login", { replace: true, state: { from, email } }), ESPERA_APOS_EXPIRAR_MS);
    return () => clearTimeout(id);
  }, [expirado, navigate, from, email]);

  useEffect(() => () => clearTimeout(timerSaida.current), []);

  async function verificar(codigo) {
    await verifyMfaLogin({ mfaToken, codigo });
    timerSaida.current = setTimeout(
      () => navigate(primeiroAcesso ? "/primeiro-acesso" : destinoFinal, { replace: true }),
      ESPERA_APOS_LIBERAR_MS,
    );
  }

  // Sem token: o useEffect já agendou a volta; null evita piscar a UI.
  if (!mfaToken) return null;

  return (
    <PainelMfa
      emailMascarado={mascararEmail(email)}
      onVoltar={() => navigate("/login", { replace: true, state: { from, email } })}
      verificar={verificar}
      expirado={expirado}
      segundosRestantes={restante}
      quemAjuda={marcaDaEntrada().quemAjuda}
    />
  );
}
